import React, { useState } from 'react';
import { usePlan } from '../../context/PlanContext';
import { Button } from '../../components/ui/button';
import { CheckCircle, X, Crown } from 'lucide-react';
import UpgradeModal from '../../components/UpgradeModal';
import AdminLayout from './AdminLayout';

function PlanUsageTab() {
    const { plan, planLabel, planData, tiers } = usePlan();
    const [showUpgrade, setShowUpgrade] = useState(false);

    if (!planData) return <div className="py-8 text-center text-stone-500">Loading plan information...</div>;

    const PLAN_COLORS = { free: 'bg-stone-100 text-stone-700', starter: 'bg-blue-100 text-blue-700', pro: 'bg-stone-900 text-white', enterprise: 'bg-amber-100 text-amber-700' };
    const RESOURCE_LABELS = {
        users: 'Team Members', contacts: 'Contacts', accounts: 'Companies', deals: 'Deals',
        booking_links: 'Booking Links', email_sequences: 'Email Sequences', deal_rooms: 'Deal Rooms', heartbeats: 'AI Heartbeats',
    };

    return (
        <div className="space-y-6" data-testid="plan-usage-tab">
            {/* Current Plan Banner */}
            <div className="flex items-center justify-between p-5 rounded-xl border border-stone-200 bg-stone-50">
                <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 rounded-lg text-sm font-bold ${PLAN_COLORS[plan] || PLAN_COLORS.free}`}>
                        {planLabel}
                    </div>
                    <div>
                        <p className="font-semibold text-stone-900">{planData.description}</p>
                        <p className="text-sm text-stone-500">${planData.price}/month per user</p>
                    </div>
                </div>
                <Button onClick={() => setShowUpgrade(true)} variant="outline" className="text-sm" data-testid="view-plans-btn">
                    View All Plans
                </Button>
            </div>

            {/* Usage Meters */}
            <div>
                <h3 className="text-sm font-semibold text-stone-500 uppercase mb-3">Resource Usage</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(planData.usage || {}).map(([key, usage]) => (
                        <div key={key} className="p-4 rounded-xl border border-stone-200" data-testid={`usage-${key}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-stone-700">{RESOURCE_LABELS[key] || key}</span>
                                <span className="text-xs text-stone-500">
                                    {usage.current} / {usage.unlimited ? 'Unlimited' : usage.limit}
                                </span>
                            </div>
                            <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${usage.percentage > 90 ? 'bg-red-500' : usage.percentage > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                    style={{ width: `${Math.min(usage.unlimited ? 0 : usage.percentage, 100)}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Features */}
            <div>
                <h3 className="text-sm font-semibold text-stone-500 uppercase mb-3">Feature Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(planData.features || {}).map(([key, enabled]) => (
                        <div key={key} className="flex items-center gap-2 p-2.5 rounded-lg border border-stone-100">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-stone-100 text-stone-400'}`}>
                                {enabled ? <CheckCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            </div>
                            <span className={`text-sm ${enabled ? 'text-stone-700' : 'text-stone-400'}`}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
        </div>
    );
}

export default function AdminPlanUsagePage() {
    return (
        <AdminLayout title="Plan & Usage" description="View your plan details and resource usage">
            <PlanUsageTab />
        </AdminLayout>
    );
}
